/**
 * Client-side `signup_completed` helper.
 *
 * Historically this event was only fired in the email + OTP flow (`VerifyOtp`),
 * so Google/Apple and invite-based signups never showed up in the funnel.
 *
 * This helper can be called from any post-authentication surface: it fires at
 * most once per user (localStorage guard) and only for genuinely new accounts,
 * so calling it from several places is safe.
 */
import type { User } from '@supabase/supabase-js';
import { track } from './bus';
import type { EventProps } from './types';

const KEY_PREFIX = 'careos:signup-tracked:';
const NEW_ACCOUNT_WINDOW_MS = 30 * 60 * 1000; // 30 min

export function isNewAccount(user: User): boolean {
  if (!user.created_at) return false;
  const created = new Date(user.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= NEW_ACCOUNT_WINDOW_MS;
}

export function signupMethodFromUser(user: User): string {
  const provider =
    (user.app_metadata?.provider as string | undefined) ??
    (user.identities?.[0]?.provider as string | undefined);
  return provider || 'unknown';
}

/** Fires `signup_completed` once per user. Returns true when it actually fired. */
export function trackSignupCompletedOnce(user: User | null, props: EventProps = {}): boolean {
  if (!user) return false;
  const key = `${KEY_PREFIX}${user.id}`;
  try {
    if (localStorage.getItem(key)) return false;
  } catch { /* storage unavailable — still fire */ }

  if (!isNewAccount(user)) {
    try { localStorage.setItem(key, '1'); } catch { /* noop */ }
    return false;
  }

  track('signup_completed', {
    method: signupMethodFromUser(user),
    source: 'client',
    ...props,
  });
  try { localStorage.setItem(key, '1'); } catch { /* noop */ }
  return true;
}

/** Marks a user as already tracked (used by flows that fire the event themselves). */
export function markSignupTracked(userId: string): void {
  try { localStorage.setItem(`${KEY_PREFIX}${userId}`, '1'); } catch { /* noop */ }
}
